import { type ReactNode, useEffect, useId, useState } from "react";
import type { InboxThread } from "../../src/inbox-v2/contract.ts";
import type { ForwardRecipients } from "../../src/inbox-v2/forward-contract.ts";
import { addressesMailbox, replyRecipients } from "../../src/inbox-v2/reply-recipients.ts";
import { fieldCapability } from "./compose-capabilities.ts";
import { FieldRow, RevealField } from "./composer.tsx";
import { parseRecipientText, type RecipientText, recipientEntry } from "./recipient-input.ts";

type Source = { messageId: string; all: boolean } | null;

function replyParent(thread: InboxThread, source: Source) {
  const selected = thread.messages.find((message) => message.id === source?.messageId);
  return selected?.direction === "inbound"
    ? selected
    : thread.messages.findLast((message) => message.direction === "inbound");
}

/** The reviewed audience and cadence derive from the same loaded thread as the seen-through marker. */
export function replyAudience(thread: InboxThread, source: Source) {
  const parent = replyParent(thread, source);
  const all = source?.all ?? true;
  const recipients = parent && thread.mailbox ? replyRecipients(parent, thread.mailbox, all) : null;
  const lastInbound = thread.messages.findLastIndex((message) => message.direction === "inbound");
  return {
    parent,
    all,
    recipients,
    messageId: parent?.id ?? thread.conversation.leadMessageId,
    selected: !!parent && parent.id !== thread.conversation.leadMessageId,
    seenThrough: thread.messages.at(-1)?.id ?? parent?.id ?? "",
    cadence: thread.messages
      .slice(lastInbound + 1)
      .filter((message) => message.direction === "outbound").length,
  };
}

/** Names known for addresses on this thread, by lowercased address: today the lead's. */
function knownNames(thread: InboxThread): Readonly<Record<string, string>> {
  const { leadAddress, leadName } = thread.conversation;
  return leadAddress && leadName ? { [leadAddress.toLowerCase()]: leadName } : {};
}

function presetText(preset: ForwardRecipients | null, thread: InboxThread): RecipientText {
  const names = knownNames(thread);
  const line = (addresses: readonly string[] = []) =>
    addresses.map((value) => recipientEntry(value, names[value.toLowerCase()])).join(", ");
  return { to: line(preset?.to), cc: line(preset?.cc), bcc: "" };
}

/**
 * A reply's To, Cc and Bcc as the rep edits them, Gmail's way: the preset fills the lines and the rep
 * may add or remove addresses (Dawid, 2026-10-23, ledger `23021d3e`). A known name is shown with its
 * address, `Name <address>`, as mail clients do; only the address is sent. Edits belong to one preset:
 * choosing another mode or message, or a confirmed send, fills the lines again.
 * `recipients` is what Send carries, parsed by the server's own schema; null while the lines are not
 * a sendable set, or when they name the mailbox the reply goes from (the server refuses that too).
 * `audience` is who the folded bar counts: the edited set while it is sendable, else the preset.
 */
export function useReplyRecipients(
  thread: InboxThread,
  audience: Pick<ReturnType<typeof replyAudience>, "parent" | "all" | "recipients">,
  send: { readonly id: string; readonly state: string } | null | undefined,
) {
  const id = useId();
  // The last send confirmed sent, kept while a newer one is held or undone: the status names only
  // the latest send, and dropping the key on Send would refill the lines with the preset.
  const [sent, setSent] = useState(send?.state === "sent" ? send.id : "");
  if (send?.state === "sent" && send.id !== sent) setSent(send.id);
  const presetKey = `${audience.parent?.id}:${audience.all}:${send?.state === "sent" ? send.id : sent}`;
  const [edited, setEdited] = useState<{ key: string; text: RecipientText } | null>(null);
  // Said once the rep leaves a line, never while they are still typing in it (forward's rule).
  const [left, setLeft] = useState(false);
  const text = edited?.key === presetKey ? edited.text : presetText(audience.recipients, thread);
  const parsed = parseRecipientText(text);
  const mailbox = thread.mailbox;
  const own = parsed.success && !!mailbox && addressesMailbox(parsed.data, mailbox);
  const recipients = parsed.success && !own ? parsed.data : null;
  return {
    id,
    text,
    recipients,
    audience: recipients ?? audience.recipients,
    own,
    wrong: left && !recipients && text.to.trim() !== "",
    change: (patch: Partial<RecipientText>) => {
      setLeft(false);
      setEdited({ key: presetKey, text: { ...text, ...patch } });
    },
    leave: () => setLeft(true),
    /** The rep changed the lines from the preset this box opened with. */
    edited: edited?.key === presetKey,
    /** A saved draft's lines put back as the rep left them, or the preset again for null. */
    restore: (saved: RecipientText | null) =>
      setEdited(saved ? { key: presetKey, text: saved } : null),
  };
}
export type ReplyRecipientEdit = ReturnType<typeof useReplyRecipients>;

/** Who a held reply goes to, for its Undo line: the lead's name while To is the lead alone. */
export function heldAudience(
  recipients: ForwardRecipients | null,
  leadAddress: string | null,
  name: string,
): string {
  const to = recipients?.to ?? [];
  const lead = leadAddress?.trim().toLowerCase();
  return to.length === 0 || (to.length === 1 && to[0]?.toLowerCase() === lead)
    ? name
    : to.join(", ");
}

/** The To, Cc and Bcc lines of the reply: inline inputs where the route lets the rep change them. */
export function ReplyRecipientRows(props: { edit: ReplyRecipientEdit; frozen: boolean }) {
  const { edit, frozen } = props;
  const { id } = edit;
  const [shown, setShown] = useState({ cc: false, bcc: false });
  const [focus, setFocus] = useState<"cc" | "bcc" | null>(null);
  useEffect(() => {
    if (focus) document.getElementById(`${id}-${focus}`)?.focus();
  }, [focus, id]);
  const ccShown = shown.cc || edit.text.cc !== "";
  const bccShown = shown.bcc || edit.text.bcc !== "";
  const input = (field: "to" | "cc" | "bcc") => (
    <input
      id={`${id}-${field}`}
      className="lcr-i2-field-input"
      // Text, not email: an email input would not hold `Name <address>`; the keyboard is still email's.
      type="text"
      inputMode="email"
      autoCapitalize="off"
      spellCheck={false}
      required={field === "to"}
      value={edit.text[field]}
      disabled={frozen}
      autoComplete="off"
      maxLength={13000}
      aria-invalid={edit.wrong && edit.text[field] !== "" ? true : undefined}
      aria-describedby={edit.wrong ? `${id}-invalid` : undefined}
      onBlur={(event) => {
        edit.leave();
        // A Cc or Bcc line left empty folds back to its quiet link, as it started (forward's rule).
        if (field !== "to" && !event.currentTarget.value.trim()) {
          setFocus(null);
          setShown((current) => ({ ...current, [field]: false }));
        }
      }}
      onChange={(event) => {
        // A line the rep is typing in stays until they leave it, even emptied.
        if (field !== "to" && !shown[field]) setShown({ ...shown, [field]: true });
        edit.change({ [field]: event.currentTarget.value });
      }}
    />
  );
  const reveal = (field: "cc" | "bcc") => {
    setShown({ ...shown, [field]: true });
    setFocus(field);
  };
  const row = (field: "to" | "cc" | "bcc", label: string, end?: ReactNode) => (
    <FieldRow
      label={label}
      inputId={`${id}-${field}`}
      capability={fieldCapability("reply", field)}
      value={edit.text[field]}
      end={end}
    >
      {input(field)}
    </FieldRow>
  );
  return (
    <>
      {row(
        "to",
        "To",
        <>
          {!ccShown && <RevealField label="Cc" disabled={frozen} onReveal={() => reveal("cc")} />}
          {!bccShown && (
            <RevealField label="Bcc" disabled={frozen} onReveal={() => reveal("bcc")} />
          )}
        </>,
      )}
      {ccShown && row("cc", "Cc")}
      {bccShown && row("bcc", "Bcc")}
    </>
  );
}

/** Why Send waits on the recipient lines, under the fields; the lines point to it. */
export function ReplyRecipientsNotice({ edit }: { edit: ReplyRecipientEdit }) {
  if (!edit.wrong) return null;
  return (
    <div className="lcr-reply-notice" data-tone="warn" role="alert" id={`${edit.id}-invalid`}>
      <p>
        {edit.own
          ? "A reply can't go to the mailbox it's sent from. Remove that address."
          : "Enter valid email addresses, with each address in only one field."}
      </p>
    </div>
  );
}
